// ========================================
// 🔧 APP.JS - COMPLETO CON SOCKET CONTEXT
// ========================================

import React, { useState, useEffect, useCallback, useRef, createContext } from 'react';
import { io } from 'socket.io-client';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import JoinRoom from './pages/JoinRoom';
import Room from './pages/MeetingRoom';
import AdminPanel from './pages/AdminPanel';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';

// ✅ CREA E ESPORTA IL SOCKET CONTEXT
export const SocketContext = createContext(null);

function App() {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);

  useEffect(() => {
    console.log('🚀 Inizializzazione LuminAI Connect...');
    console.log('🌐 Connessione a:', SERVER_URL);

    if (socketRef.current) {
      console.log('⚠️ Socket già esistente, skip inizializzazione');
      return;
    }

    const newSocket = io(SERVER_URL, {
      transports: ['websocket'],
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000
    });

    // ✅ Esponi socket globalmente per debug
    window._socket = newSocket;
    socketRef.current = newSocket;

    // ✅ FIX HEARTBEAT - Evento connect
    newSocket.on('connect', () => {
      console.log('✅ Connesso al server - Socket ID:', newSocket.id);
      setConnected(true);
      setLoading(false);
      
      // ✅ CRITICO: Avvia heartbeat automatico
      const heartbeatInterval = setInterval(() => {
        if (newSocket.connected) {
          newSocket.emit('heartbeat');
          console.log('💓 Heartbeat inviato');
        } else {
          console.warn('⚠️ Socket disconnesso, fermo heartbeat');
          clearInterval(heartbeatInterval);
        }
      }, 10000); // Ogni 10 secondi (server timeout = 30s)
      
      // Salva reference per cleanup
      newSocket._heartbeatInterval = heartbeatInterval;
      console.log('✅ Heartbeat automatico attivato (ogni 10s)');
    });

    // ✅ FIX HEARTBEAT - Evento disconnect
    newSocket.on('disconnect', (reason) => {
      console.log('❌ Disconnesso dal server:', reason);
      setConnected(false);
      
      // ✅ CRITICO: Cleanup heartbeat interval
      if (newSocket._heartbeatInterval) {
        console.log('🧹 Cleanup heartbeat interval');
        clearInterval(newSocket._heartbeatInterval);
        newSocket._heartbeatInterval = null;
      }
      
      // Analisi motivo disconnessione
      if (reason === 'io server disconnect') {
        console.error('🚫 Server ha forzato disconnessione (possibile timeout)');
      } else if (reason === 'transport close') {
        console.warn('🔌 Connessione di rete persa');
      } else if (reason === 'ping timeout') {
        console.error('⏱️ Timeout ping - connessione troppo lenta');
      }
    });

    // ✅ FIX HEARTBEAT - Evento reconnect
    newSocket.on('reconnect', (attemptNumber) => {
      console.log(`🔄 Riconnesso dopo ${attemptNumber} tentativo/i`);
      setConnected(true);
      
      // ✅ CRITICO: Riavvia heartbeat dopo reconnect
      const heartbeatInterval = setInterval(() => {
        if (newSocket.connected) {
          newSocket.emit('heartbeat');
          console.log('💓 Heartbeat inviato (dopo reconnect)');
        } else {
          clearInterval(heartbeatInterval);
        }
      }, 10000);
      
      newSocket._heartbeatInterval = heartbeatInterval;
      console.log('✅ Heartbeat riavviato dopo reconnect');
    });

    // Eventi di reconnection
    newSocket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`🔄 Tentativo di riconnessione ${attemptNumber}...`);
    });

    newSocket.on('reconnect_error', (error) => {
      console.error('❌ Errore riconnessione:', error.message);
    });

    newSocket.on('reconnect_failed', () => {
      console.error('❌ Riconnessione fallita dopo 5 tentativi');
      setLoading(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ Errore connessione:', error.message);
      setLoading(false);
    });

    setSocket(newSocket);

    // ✅ Cleanup finale
    return () => {
      if (newSocket) {
        console.log('🔌 Chiusura connessione socket...');
        
        // ✅ CRITICO: Cleanup heartbeat prima di chiudere
        if (newSocket._heartbeatInterval) {
          console.log('🧹 Cleanup heartbeat finale');
          clearInterval(newSocket._heartbeatInterval);
          newSocket._heartbeatInterval = null;
        }
        
        newSocket.close();
        socketRef.current = null;
        window._socket = null;
      }
    };
  }, []);

  // Callback per join room
  const handleJoinRoom = useCallback((roomData) => {
    console.log('🚪 Join room richiesto:', roomData);
  }, []);

  if (loading) {
    return (
      <div className="app-loading" style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        color: '#FF6B35',
        fontSize: '1.2rem',
        fontWeight: 500
      }}>
        <div className="loading-spinner" style={{
          width: '40px',
          height: '40px',
          border: '3px solid #333',
          borderTop: '3px solid #FF6B35',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }}></div>
        <p>Connessione al server...</p>
      </div>
    );
  }

  return (
    <SocketContext.Provider value={{ socket, connected }}>
      <Router>
        <div className="App">
          <Routes>
            <Route 
              path="/" 
              element={
                <JoinRoom 
                  socket={socket} 
                  onJoinRoom={handleJoinRoom}
                  connected={connected}
                />
              } 
            />
            <Route 
              path="/join/:roomId" 
              element={
                <JoinRoom 
                  socket={socket} 
                  onJoinRoom={handleJoinRoom}
                  connected={connected}
                />
              } 
            />
            <Route 
              path="/room/:roomId" 
              element={
                <Room 
                  socket={socket}
                  connected={connected}
                />
              } 
            />
            <Route 
              path="/admin" 
              element={<AdminPanel />} 
            />
          </Routes>
        </div>
      </Router>
    </SocketContext.Provider>
  );
}

export default App;
